import type { ChangeEvent } from "react";

import Sentry from "@similarweb/sentry-logger";

import {
    buildAiTopicKeywordListId,
    buildKeywordGroupKeywordListId,
} from "app/modules/demand-analysis/keyword-lists/id-builders";
import { getDefaultParamsForState } from "app/pages/digital-suite/home-page/helpers";
import { allTrackers } from "app/services/track/track";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type {
    AutocompleteDemandAnalysisItem,
    AutocompleteDemandAnalysisEntity,
} from "../types/AutocompleteDemandAnalysisTypes";
import { AutocompleteDemandAnalysisEntityType } from "../types/AutocompleteDemandAnalysisTypes";

const VALID_ITEM_TYPES: ReadonlySet<string> = new Set(Object.values(AutocompleteDemandAnalysisEntityType));

export const isValidAutocompleteItem = (item: unknown): item is AutocompleteDemandAnalysisItem =>
    item != null && VALID_ITEM_TYPES.has((item as AutocompleteDemandAnalysisItem).type);

export const handleItemNavigation = (item: AutocompleteDemandAnalysisItem, countryId: number) => {
    switch (item.type) {
        case AutocompleteDemandAnalysisEntityType.ownList:
        case AutocompleteDemandAnalysisEntityType.sharedList: {
            allTrackers.dispatchTrackEvent("Select keyword list", "click", "Auto complete", {
                target_KW_list_type: "Keyword list",
                target_KW_list_name: item.originalData.Name,
                target_KW_list_shared_with_me: item.originalData.SharedWithMe,
                target_KW_list_ID: item.originalData.Id,
            });

            const keywordListId = buildKeywordGroupKeywordListId(item.originalData.Id);

            void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, {
                ...getDefaultParamsForState(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE),
                id: [keywordListId],
                country: countryId,
            });
            break;
        }

        case AutocompleteDemandAnalysisEntityType.aiTopic: {
            allTrackers.dispatchTrackEvent("Select keyword list", "click", "Auto complete", {
                target_KW_list_type: "AI Topic",
                target_KW_list_name: item.name,
            });

            const keywordListId = buildAiTopicKeywordListId(item.name);

            void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, {
                ...getDefaultParamsForState(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE),
                id: [keywordListId],
                country: countryId,
            });
            break;
        }

        default:
            Sentry.errorTracker({
                message: "Unknown autocomplete item type in navigation",
                e: new Error("Unknown item type"),
                params: {
                    extra: {
                        item: JSON.stringify(item),
                    },
                },
            });
    }
};

export const handleKeyboardSelect = (
    event: ChangeEvent<HTMLInputElement>,
    selectedItemObj: AutocompleteDemandAnalysisItem | null,
    countryId: number,
    onKeyboardSelect?: (entity: AutocompleteDemandAnalysisEntity) => void,
) => {
    if (selectedItemObj && isValidAutocompleteItem(selectedItemObj)) {
        if (onKeyboardSelect) {
            onKeyboardSelect(selectedItemObj.originalData);
        }

        handleItemNavigation(selectedItemObj, countryId);
        return;
    }

    const text = event.target?.value;
    if (text && text.trim() !== "") {
        const fallbackAiTopic = { seed: text.trim() };

        if (onKeyboardSelect) {
            onKeyboardSelect(fallbackAiTopic);
        }

        const keywordListId = buildAiTopicKeywordListId(text.trim());

        void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, {
            ...getDefaultParamsForState(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE),
            id: [keywordListId],
            country: countryId,
        });
    }
};

export const getSelectableItems = (data: AutocompleteDemandAnalysisItem[]) => {
    return data;
};
